import React, { useMemo, useState } from "react";
import {
  ArrowsPointingOutIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import CampaignRunCard, { OnAirPill } from "./CampaignRunCard";
import CoverageMapCanvas from "./CoverageMapCanvas";
import CoverageDetailsModal from "../modals/CoverageDetailsModal";
import type { CoverageDetailsScope } from "../modals/CoverageDetailsModal";
import { displayCreativeName } from "./campaignFormatting";
import {
  COVERAGE_STATUSES,
  DEFAULT_COVERAGE_STATUSES,
  STATUS_COLOR,
  countScreensByStatus,
  filterByCampaign,
  filterByStatus,
  groupCoverage,
  hasPosition,
  cityOf,
  listCampaigns,
  panelPathFor,
  regionOf,
  strongestStatus,
} from "../../utils/coverageMap";
import type { CoverageTotals } from "../../utils/coverageMap";
import type { CampaignStatus, MapScreen } from "../../schemas/campaignSchema";

interface CoverageMapProps {
  screens: MapScreen[];
  /** The brand owns more creatives than the map walks. */
  truncated: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * The map and its panel fill what is left of the screen, so the map is visible
 * without scrolling. The brand layout's header is 10vh; the rest (page padding,
 * title row, stats strip, filter row) comes to about 13.5rem. On a short window
 * the minimum wins and the page scrolls instead of squashing the map.
 */
const PANE_HEIGHT = "h-[calc(90vh-13.5rem)] min-h-[22rem]";

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

const StatusDot: React.FC<{ status: CampaignStatus }> = ({ status }) => (
  <span
    className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
    style={{ backgroundColor: STATUS_COLOR[status] }}
  />
);

const PANEL_ROW =
  "flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left hover:border-gray-300";

const PanelHeading: React.FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => (
  <h3 className="mb-2 text-sm font-semibold text-gray-800">
    {title}
    {subtitle && (
      <span className="ml-2 font-normal text-xs text-gray-500">{subtitle}</span>
    )}
  </h3>
);

/**
 * A city or an area: the same row either way, because they are the same step
 * of the same walk down to a screen.
 */
const PlaceButton: React.FC<{
  name: string;
  totals: CoverageTotals;
  onClick: () => void;
}> = ({ name, totals, onClick }) => (
  <button type="button" onClick={onClick} className={PANEL_ROW}>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm font-medium text-gray-900">
        {name}
      </span>
      <span className="block text-xs text-gray-500">
        <b className="font-semibold text-gray-700">{totals.screenCount}</b>{" "}
        {totals.screenCount === 1 ? "screen" : "screens"} ·{" "}
        <b className="font-semibold text-gray-700">{totals.campaigns}</b>{" "}
        {totals.campaigns === 1 ? "campaign" : "campaigns"} ·{" "}
        <b className="font-semibold text-gray-700">
          {totals.plays.toLocaleString()}
        </b>{" "}
        plays
      </span>
    </span>
    {totals.onAir > 0 && (
      <span className="flex-shrink-0 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600">
        {totals.onAir} on air
      </span>
    )}
    <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
  </button>
);

const BackButton: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex min-w-0 items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-900"
  >
    <ChevronLeftIcon className="h-4 w-4 flex-shrink-0" />
    <span className="truncate">{children}</span>
  </button>
);

/**
 * The top of every panel step: the way back (or the heading) on the left, and
 * "View details" on the right, which opens the same place in a large popup.
 */
const PanelTop: React.FC<{ onView: () => void; children: React.ReactNode }> = ({
  onView,
  children,
}) => (
  <div className="mb-3 flex items-center justify-between gap-2">
    {children}
    <button
      type="button"
      onClick={onView}
      className="inline-flex flex-shrink-0 items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
    >
      <ArrowsPointingOutIcon className="h-3.5 w-3.5" />
      View details
    </button>
  </div>
);

const ScreenDetail: React.FC<{ screen: MapScreen }> = ({ screen }) => {
  // Screen names usually start with the restaurant's, so it is not repeated.
  const restaurant =
    screen.restaurantName && !screen.screenName.includes(screen.restaurantName)
      ? screen.restaurantName
      : null;
  const location = [restaurant, screen.region, screen.city]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="break-words font-semibold text-gray-900">
            {screen.screenName}
          </p>
          {location && <p className="truncate text-xs text-gray-500">{location}</p>}
          {screen.address && (
            <p className="mt-0.5 text-xs text-gray-500">{screen.address}</p>
          )}
        </div>
        <span
          className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
            screen.screenOnline
              ? "bg-success-100 text-success-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {screen.screenOnline ? "Online" : "Offline"}
        </span>
      </div>

      {!hasPosition(screen) && (
        <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-700">
          This screen has no map location yet, so it is not shown as a pin.
        </p>
      )}

      <ul className="mt-3 flex flex-col gap-2">
        {screen.campaigns.map((campaign) => (
          <CampaignRunCard key={campaign.creativeId} campaign={campaign} />
        ))}
      </ul>
    </div>
  );
};

/**
 * The brand's coverage map: where its campaigns run, filterable by Live /
 * Upcoming / Expired, with a city rollup and per-screen detail beside it.
 *
 * Purely presentational — the caller owns the fetch. Status filtering happens
 * here, in the browser, over one unfiltered payload, so the buttons, their
 * counts and the pins can never disagree.
 */
const CoverageMap: React.FC<CoverageMapProps> = ({
  screens,
  truncated,
  isLoading,
  isError,
}) => {
  const [statuses, setStatuses] = useState<Set<CampaignStatus>>(
    () => new Set(DEFAULT_COVERAGE_STATUSES),
  );
  const [creativeId, setCreativeId] = useState("");
  // The panel walks the same path as the list view: city -> area -> screen ->
  // its campaigns. A click on a pin jumps straight to the last step.
  const [city, setCity] = useState<string | null>(null);
  const [region, setRegion] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const campaignOptions = useMemo(() => listCampaigns(screens), [screens]);
  // A refresh can retire the campaign being filtered on; fall back to "all".
  const activeCreativeId = campaignOptions.some(
    (o) => o.creativeId === creativeId,
  )
    ? creativeId
    : "";

  const scoped = useMemo(
    () => filterByCampaign(screens, activeCreativeId),
    [screens, activeCreativeId],
  );
  const counts = useMemo(() => countScreensByStatus(scoped), [scoped]);
  const visible = useMemo(() => filterByStatus(scoped, statuses), [scoped, statuses]);

  const located = useMemo(() => visible.filter(hasPosition), [visible]);
  const cities = useMemo(() => groupCoverage(visible), [visible]);

  const selectedScreens = useMemo(
    () => visible.filter((s) => selectedIds.includes(s.screenId)),
    [visible, selectedIds],
  );
  // A filter change can retire whatever was open, so the path is re-resolved
  // against the current data rather than trusted.
  const activeCity = cities.find((c) => c.city === city) ?? null;
  const activeRegion =
    activeCity?.regions.find((r) => r.region === region) ?? null;

  // Frame what the panel is showing: a whole city, or one area within it.
  const framed = activeRegion?.screens ?? activeCity?.regions.flatMap((r) => r.screens);
  const frameIds = useMemo(
    () => (framed ? framed.filter(hasPosition).map((s) => s.screenId) : []),
    [framed],
  );

  const totalCampaigns = useMemo(
    () => new Set(visible.flatMap((s) => s.campaigns.map((c) => c.creativeId))).size,
    [visible],
  );
  const unlocatedCount = visible.length - located.length;

  const toggleStatus = (status: CampaignStatus) =>
    setStatuses((current) => {
      const next = new Set(current);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });

  // A cluster click walks the panel the same way a city/area click does: to
  // the city its screens share, and on to the area if they share that too.
  // Screens spread over several cities leave the panel where it is.
  const zoomInto = (screenIds: string[]) => {
    const ids = new Set(screenIds);
    const path = panelPathFor(visible.filter((s) => ids.has(s.screenId)));
    if (!path.city) return;
    setCity(path.city);
    setRegion(path.region);
    setSelectedIds([]);
  };

  // "View details" opens the popup on what the panel shows, with the page's
  // filters. A pin click can land on a screen without walking the panel, so a
  // selection supplies its own city and area rather than trusting the panel's.
  const [details, setDetails] = useState<CoverageDetailsScope | null>(null);
  const openDetails = () => {
    const place =
      selectedScreens.length === 1
        ? {
            city: cityOf(selectedScreens[0]),
            region: regionOf(selectedScreens[0]),
            screenId: selectedScreens[0].screenId,
          }
        : selectedScreens.length > 1
          ? { ...panelPathFor(selectedScreens), screenId: null }
          : {
              city: activeCity?.city ?? null,
              region: activeRegion?.region ?? null,
              screenId: null,
            };
    setDetails({ ...place, statuses, creativeId: activeCreativeId });
  };

  if (isLoading) {
    return (
      <p className="px-1 py-10 text-center text-sm text-gray-500">
        Loading your coverage…
      </p>
    );
  }

  if (isError) {
    return (
      <p className="px-1 py-10 text-center text-sm text-red-600">
        Your coverage map could not be loaded. Try refreshing.
      </p>
    );
  }

  if (screens.length === 0) {
    return (
      <p className="px-1 py-10 text-center text-sm text-gray-500">
        None of your campaigns are on a screen yet. Once your media house
        schedules them, the screens appear here.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {COVERAGE_STATUSES.map(({ status, label, hint, color }) => {
          const on = statuses.has(status);
          return (
            <button
              key={status}
              type="button"
              aria-pressed={on}
              title={`${hint}. ${plural(counts[status], "screen")}.`}
              onClick={() => toggleStatus(status)}
              className={`flex items-center gap-2 rounded border px-3 py-2 text-sm font-medium transition-all duration-150 ${
                on
                  ? "border-primary-default bg-primary-default text-white"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              <span
                className="h-2.5 w-2.5 rounded-full border border-white"
                style={{ backgroundColor: color }}
              />
              {label}
              <span
                className={`rounded-full px-1.5 text-xs ${
                  on ? "bg-white/25" : "bg-gray-100 text-gray-600"
                }`}
              >
                {counts[status]}
              </span>
            </button>
          );
        })}

        <select
          value={activeCreativeId}
          onChange={(e) => setCreativeId(e.target.value)}
          aria-label="Filter by campaign"
          className="ml-1 max-w-[16rem] rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700"
        >
          <option value="">All campaigns</option>
          {campaignOptions.map((o) => (
            <option key={o.creativeId} value={o.creativeId}>
              {displayCreativeName(o.name)}
            </option>
          ))}
        </select>

        <span className="ml-auto flex items-center gap-3 text-xs text-gray-500">
          <span>
            {plural(visible.length, "screen")} · {plural(totalCampaigns, "campaign")}
          </span>
        </span>
      </div>

      {truncated && (
        <p className="rounded bg-amber-50 px-3 py-2 text-xs text-amber-700">
          You have more campaigns than the map can show at once. It covers your
          500 most recent.
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className={`relative ${PANE_HEIGHT}`}>
          <CoverageMapCanvas
            screens={located}
            selectedIds={selectedIds}
            frameIds={frameIds}
            onSelect={setSelectedIds}
            onZoomInto={zoomInto}
          />
          {visible.length === 0 && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-white/70 p-6 text-center text-sm text-gray-600">
              {statuses.size === 0
                ? "Turn on Live, Upcoming or Expired to see screens."
                : "No screens match these filters."}
            </div>
          )}
        </div>

        <aside
          className={`${PANE_HEIGHT} overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-3`}
        >
          {selectedScreens.length > 0 ? (
            <>
              <PanelTop onView={openDetails}>
                <BackButton onClick={() => setSelectedIds([])}>
                  {activeRegion?.region ?? activeCity?.city ?? "All cities"}
                </BackButton>
              </PanelTop>
              <div className="flex flex-col gap-3">
                {selectedScreens.map((screen) => (
                  <ScreenDetail key={screen.screenId} screen={screen} />
                ))}
              </div>
            </>
          ) : activeRegion ? (
            <>
              <PanelTop onView={openDetails}>
                <BackButton onClick={() => setRegion(null)}>
                  {activeCity?.city}
                </BackButton>
              </PanelTop>
              <PanelHeading
                title={activeRegion.region}
                subtitle={plural(activeRegion.screenCount, "screen")}
              />
              <ul className="flex flex-col gap-1.5">
                {activeRegion.screens.map((screen) => (
                  <li key={screen.screenId}>
                    <button
                      type="button"
                      onClick={() => setSelectedIds([screen.screenId])}
                      className={PANEL_ROW}
                    >
                      <StatusDot status={strongestStatus(screen.campaigns)} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-900">
                          {screen.screenName}
                        </span>
                        <span className="block truncate text-xs text-gray-500">
                          {screen.restaurantName ?? screen.address ?? ""}
                        </span>
                      </span>
                      {screen.campaigns.some((c) => c.isPlayingNow) && <OnAirPill />}
                      {!hasPosition(screen) && (
                        <span title="No map location yet">
                          <MapPinIcon className="h-4 w-4 text-gray-300" />
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : activeCity ? (
            <>
              <PanelTop onView={openDetails}>
                <BackButton onClick={() => setCity(null)}>All cities</BackButton>
              </PanelTop>
              <PanelHeading
                title={activeCity.city}
                subtitle={`${plural(activeCity.regions.length, "area")} · ${plural(
                  activeCity.screenCount,
                  "screen",
                )}`}
              />
              <ul className="flex flex-col gap-1.5">
                {activeCity.regions.map((row) => (
                  <li key={row.region}>
                    <PlaceButton
                      name={row.region}
                      totals={row}
                      onClick={() => {
                        setRegion(row.region);
                        setSelectedIds([]);
                      }}
                    />
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <PanelTop onView={openDetails}>
                <h3 className="text-sm font-semibold text-gray-800">
                  Coverage by city
                </h3>
              </PanelTop>
              <ul className="flex flex-col gap-1.5">
                {cities.map((row) => (
                  <li key={row.city}>
                    <PlaceButton
                      name={row.city}
                      totals={row}
                      onClick={() => {
                        setCity(row.city);
                        setRegion(null);
                        setSelectedIds([]);
                      }}
                    />
                  </li>
                ))}
              </ul>
              {unlocatedCount > 0 && (
                <p className="mt-3 text-xs text-gray-500">
                  {plural(unlocatedCount, "screen")} not shown as pins: no map
                  location set yet. They are still counted and listed by city.
                </p>
              )}
            </>
          )}
        </aside>
      </div>

      {details && (
        <CoverageDetailsModal
          isOpen
          onClose={() => setDetails(null)}
          screens={screens}
          initial={details}
        />
      )}
    </div>
  );
};

export default CoverageMap;
